import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import PropTypes from 'prop-types';
import styled from 'styled-components';
import { ZDivider } from '@zanichelli/albe-web-components/react';

import { exercisePropTypes } from '../../../../constants/exercise';
import { RESULTS } from '../../../../constants/routes';
import { hideModal } from '../../../../actions/modal';
import { terminateHomework, terminateSection, correctAllExercises } from '../../../../actions/status';
import { useOnlineStatus } from '../../../../lib/hooks';
import Spacer from '../../../spacer';

import { Content, ModalBodyContainer } from '../../components';
import Buttons from '../../buttons';
import Modal from '../..';
import { DividerSize } from '@zanichelli/albe-web-components';
import { currentSectionIsLast, getCurrentSection } from '../../../../lib/status';

export const Message = styled.span``;

export const SummaryMessage = styled.span`
    text-transform: uppercase;
`;

export const ErrorMessage = styled.span`
    text-transform: uppercase;
    color: var(--red700);
`;

export const MissingExercisesNumber = styled.span`
    font-size: 22px;
    line-height: 20px;
    font-weight: 600;
`;

const countMissingExercises = (numberOfExercises, answers) => {
    const completedExercises = answers.filter(({ answer }) => answer.length > 0 || answer.finalAnswer);
    return numberOfExercises - completedExercises.length;
};

const getSummary = (countMissing, onlineStatus, sections) => {
    if (!onlineStatus) {
        let message = 'Sei offline? Controlla la connessione per terminare la ';
        if (!!sections) {
            message += currentSectionIsLast(sections) ? 'sezione e la prova.' : 'sezione.';
        } else {
            message += 'prova.';
        }

        return <ErrorMessage className="body-4-sb">{message}</ErrorMessage>;
    }
    if (countMissing === 0) {
        return (
            <SummaryMessage className="body-4-sb">
                {'Hai svolto tutti gli esercizi'}
                {!!sections ? ' in questa sezione' : ''}
            </SummaryMessage>
        );
    }
    return (
        <SummaryMessage className="body-4-sb">
            {'Non hai svolto '}
            <MissingExercisesNumber>{countMissing}</MissingExercisesNumber>
            {countMissing === 1 ? ' esercizio' : ' esercizi'}
            {!!sections ? ' in questa sezione' : ''}
        </SummaryMessage>
    );
};

export const EndHomeworkModalBody = ({
    correctAllExercises,
    exercises,
    onClose,
    saveStatus,
    source,
    status,
    terminateHomework,
    terminateSection,
}) => {
    const history = useNavigate();
    const answers = status.answers;

    const sections = status.sections;
    const isLastSection = !!sections && currentSectionIsLast(sections);

    let countMissing = 0;
    if (!!sections) {
        const currentSection = getCurrentSection(sections);
        if (!!currentSection) {
            let firstExerciseIndex = 0;
            for (let i = 0; i < sections.length - 1; i++) {
                if (sections[i] === currentSection) {
                    break;
                }
                firstExerciseIndex += sections[i].exercisesCount;
            }
            const exercisesIds = exercises
                .slice(firstExerciseIndex, firstExerciseIndex + currentSection.exercisesCount)
                .map((exercise) => exercise.id);
            const sectionAnswers = answers.filter((answer) => exercisesIds.includes(answer.exerciseId));
            countMissing = countMissingExercises(currentSection.exercisesCount, sectionAnswers);
        }
    } else {
        countMissing = countMissingExercises(exercises.length, answers);
    }

    useEffect(() => {
        if (!status.httpStatus.errorbody && status.finished) {
            correctAllExercises(exercises, answers);
            onClose();
            setTimeout(history, 0, RESULTS, { replace: true });
        }
    }, [onClose, status.httpStatus.errorbody, status.finished, exercises, answers, history, correctAllExercises]);

    const onlineStatus = useOnlineStatus();

    const onConfirm = async () => {
        if (!!sections) {
            await terminateSection(status.id, false);
            if (!isLastSection) {
                onClose();
                return;
            }
        }
        terminateHomework(exercises, answers, status, saveStatus, source);
    };

    const InfoMessage = getSummary(countMissing, onlineStatus, sections);

    let modalTitle;
    let buttonText;
    let errorText =
        'Abbiamo un problema temporaneo con il salvataggio delle risposte. Non chiudere questa finestra e prova a cliccare di nuovo su "termina';
    if (!!sections) {
        if (isLastSection) {
            modalTitle = 'Vuoi terminare sezione e prova?';
            buttonText = 'Termina sezione e prova';
            errorText += ' sezione e prova"';
        } else {
            modalTitle = 'Vuoi terminare la sezione?';
            buttonText = 'Termina sezione';
            errorText += ' sezione"';
        }
    } else {
        modalTitle = 'Vuoi terminare la prova?';
        buttonText = 'Termina';
        errorText += '"';
    }

    return (
        <Modal title={modalTitle} onClose={onClose}>
            <ModalBodyContainer>
                <Content>
                    {status.httpStatus.errorbody ? <ErrorMessage className="body-4-sb">{errorText}</ErrorMessage> : InfoMessage}
                    <Spacer size="base" />
                    <ZDivider size={DividerSize.SMALL} color="gray800" />
                    <Spacer size="base" />
                    <Message className="body-3">
                        {!!sections ? (
                            <>
                                {
                                    'Se termini ora la sezione non potrai più tornare indietro. Perderai il tempo rimasto e non potrai più modificare le risposte date.'
                                }
                                {isLastSection ? (
                                    <>
                                        <br />
                                        {
                                            "Terminando questa sezione terminerai anche l'intera prova e potrai vedere i tuoi risultati. Cosa vuoi fare?"
                                        }
                                    </>
                                ) : (
                                    ' Cosa vuoi fare?'
                                )}
                            </>
                        ) : countMissing > 0 ? (
                            'Se termini ora la prova non potrai più modificare le risposte. Cosa vuoi fare?'
                        ) : (
                            'Termina la prova per vedere i risultati. Se ne hai bisogno puoi ancora modificare le risposte. Cosa vuoi fare?'
                        )}
                    </Message>
                </Content>
                <Buttons
                    primaryButtonClick={onConfirm}
                    primaryButtonDisabled={!onlineStatus}
                    primaryButtonText={buttonText}
                    tertiaryButtonClick={onClose}
                    tertiaryButtonText="Torna agli esercizi"
                />
            </ModalBodyContainer>
        </Modal>
    );
};

EndHomeworkModalBody.propTypes = {
    correctAllExercises: PropTypes.func,
    exercises: PropTypes.arrayOf(PropTypes.object),
    onClose: PropTypes.func.isRequired,
    saveStatus: PropTypes.bool,
    source: PropTypes.string,
    status: PropTypes.object,
    terminateHomework: PropTypes.func,
    terminateSection: PropTypes.func,
};

const actionsCreator = {
    correctAllExercises,
    onClose: hideModal,
    terminateHomework,
    terminateSection,
};

const mapStateToProps = (state) => {
    return {
        exercises: state.homework.exercises,
        status: state.status,
        saveStatus: state.application.saveStatus,
        source: state.application.source,
    };
};

export default connect(mapStateToProps, actionsCreator)(EndHomeworkModalBody);
